import React from 'react';
import classnames from 'classnames';

import StrapiButton from '../../StrapiLink/StrapiButton';
import {
  StrapiImageFragment,
  Maybe,
  Strapi_ComponentAtomLink,
  LinkComponentFragment,
  WebsiteLocaleFragment,
} from '../../../../graphql.schema';
import Image from '../../Image';
import { Breadcrumbs } from './Breadcrumbs';
import { GlobalPageContext } from '../../../types';
import { CheckMarkCircle } from '../../Icons/CheckMarkCircle';
import { Reviews } from '../../Reviews/Reviews';

import * as styles from './styles.module.scss';

type HeroCourseProps = {
  name?: string;
  image?: Maybe<StrapiImageFragment>;
  short?: string | null;
  courseCTA?: Maybe<Strapi_ComponentAtomLink>;
  icon?: 'Arrow' | 'Chevron';
  breadcrumbs: GlobalPageContext['breadcrumb'];
  courseHighlights?: {
    course_highlight: {
      title: string;
      description: string;
      image?: StrapiImageFragment;
    }[];
    course_focus_title: string;
    course_focus: {
      text: string;
    }[];
    cta: LinkComponentFragment;
  };
  websiteLocale?: WebsiteLocaleFragment;
};

export const StrapiHeroCourse_v2 = ({
  name,
  image,
  short,
  courseCTA,
  icon,
  breadcrumbs,
  courseHighlights,
  websiteLocale,
}: HeroCourseProps) => {
  const elements = breadcrumbs?.location?.split('/').filter(Boolean);
  const breadcrumbItems =
    elements?.map((item, index, arr) => {
      if (index === arr.length - 1) {
        return { href: '', text: name ?? '' };
      }
      return {
        href: `/${elements.slice(0, index + 1).join('/')}`,
        text: item.replace(/-/g, ' '),
      };
    }) || [];

  return (
    <>
      <div className={styles.root}>
        <div className={styles.content}>
          <Breadcrumbs breadcrumbs={breadcrumbItems} />
          <h1 className="title brand-a is-3">{name ?? ''}</h1>
          <p className={classnames('is-text-2', styles.content_body)}>{short}</p>

          {courseCTA ? (
            <div className={styles.content_link_container}>
              <StrapiButton icon={icon} type="primary" openInNewWindow={courseCTA.open_in_new_window} {...courseCTA} />
            </div>
          ) : null}

          {websiteLocale?.reviews_summary ? <Reviews {...websiteLocale.reviews_summary} /> : null}
        </div>

        {image ? (
          <div className={styles.image_container}>
            <Image image={image} className={styles.image} loading="lazy" />
          </div>
        ) : null}
      </div>

      {courseHighlights ? (
        <div className={styles.why_learn_root}>
          <div className={styles.why_learn_body}>
            {courseHighlights?.course_highlight.map((item) => {
              return (
                <div key={item.title}>
                  {item.image ? (
                    <img
                      className={styles.icon}
                      loading="lazy"
                      alt={item.image.alternativeText ?? ''}
                      src={item.image.url}
                    />
                  ) : null}

                  <p className={styles.title}>{item.title}</p>
                  <p className={classnames('is-text-2', styles.description)}>{item.description}</p>
                </div>
              );
            })}
          </div>

          <div className={styles.why_learn_footer}>
            <div className={styles.why_learn_footer_features}>
              <p className={styles.why_learn_footer_features_title}>{courseHighlights.course_focus_title}</p>

              <div className={styles.why_learn_footer_features_list}>
                {courseHighlights.course_focus.map((item) => (
                  <div key={item.text} className={styles.why_learn_footer_features_item}>
                    <CheckMarkCircle />
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            {courseHighlights.cta ? <StrapiButton icon="Arrow" type="primary" {...courseHighlights.cta} /> : null}
          </div>
        </div>
      ) : null}
    </>
  );
};
